import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
// @mui
import { Avatar, Box, Button, Checkbox, Chip, IconButton, TableRow, TableCell, Tooltip, Typography, Switch, FormControlLabel } from '@mui/material';
// components
import { useNavigate } from 'react-router-dom';
import { Iconify } from '../../../../components';
import capitalizeFirstLetter from '../../../../utils/capitalizedFunc';
// styles
import { ROLE_COLORS, userTableRowStyle } from '../../../../styles/PracticeUserListStyle';
// redux
import { useSelector } from '../../../../redux/store';
// routes
import { PATH_DASHBOARD } from '../../../../routes/paths';
// utils
import { callApi } from '../../../../utils/apiCall';

// ----------------------------------------------------------------------

export default function UserTableListRow({ row, selected, onSelectRow, onEditRow, onDeleteRow, inviteUser, handleSwitchChange, adminUser, currentUser }) {
    const navigate = useNavigate();
    const { pmsEnabledGlobal } = useSelector((state) => state.pmsSchedule);
    const [extraRoles, setExtraRoles] = useState([]);

    useEffect(() => {
        if (row?.id) {
            callApi(`/api/v1/permissions/practice-users/${row.id}/roles/`)
                .then((res) => {
                    if (res.status === 200) {
                        setExtraRoles((res.data?.additional_roles || []).map((r) => typeof r === 'string' ? r : r.role));
                    }
                })
                .catch(() => {});
        }
    }, [row?.id]);

    return (
        <TableRow hover selected={selected} sx={userTableRowStyle}>
            <TableCell padding="checkbox">
                <Checkbox checked={selected} onChange={onSelectRow} />
            </TableCell>
            {!inviteUser &&
                <TableCell sx={{ display: 'flex', alignItems: 'center' }}>
                    {row.user && row.user.avatar === null ?
                        <Avatar alt={''} src={row.user.avatar} sx={{ mr: 2 }} /> :
                        <Avatar alt={row.user && row.user.first_name ? row.user.first_name : row.user && row.user.salutation} src={row.user && row.user.avatar} sx={{ mr: 2 }} />
                    }
                    <Typography variant="subtitle2" noWrap>
                        {row?.user?.salutation && `${capitalizeFirstLetter(row.user && row.user.salutation)}.`} {row.user && row.user.first_name} {row.user && row.user.last_name}
                    </Typography>
                </TableCell>}
            <TableCell align="left">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                    <Chip
                        label={row.role === 'TCO' ? row.role : capitalizeFirstLetter(row.role.toLowerCase())}
                        size="small"
                        sx={{
                            fontWeight: 600,
                            fontSize: '12px',
                            ...ROLE_COLORS[row.role],
                        }}
                    />
                    {extraRoles.map((r) => (
                        <Chip
                            key={r}
                            label={r}
                            size="small"
                            sx={{ height: 24, fontSize: '12px' }}
                        />
                    ))}
                </Box>
            </TableCell>
            {!inviteUser &&
                <TableCell align="left">
                    {capitalizeFirstLetter(row.user && row.user.job_title)}
                </TableCell>}
            <TableCell align="left" >
                {inviteUser ? row.email : row.user && row.user.email}
            </TableCell>
            {!inviteUser &&
                <>
                    <TableCell align="left">
                        {row.user && row.user.phone}
                    </TableCell>

                    <TableCell align="left">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: 8, height: 8, borderRadius: '50%',
                                bgcolor: row?.is_enabled ? 'success.main' : 'error.main',
                            }} />
                            <FormControlLabel
                                control={<Switch checked={row?.is_enabled} disabled={(adminUser !== 'ADMIN' && row.role === 'ADMIN') || (currentUser === row?.user?.email)} />}
                                onChange={() => handleSwitchChange(row)}
                                label={<Typography variant="body2" sx={{ color: row?.is_enabled ? 'success.dark' : 'text.disabled' }}>
                                    {row?.is_enabled ? 'Active' : 'Disabled'}
                                </Typography>}
                            />
                        </Box>
                    </TableCell>
                </>
            }
            {!inviteUser &&
                <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5 }}>
                        {pmsEnabledGlobal && row.role === 'Practitioner' && (
                            <Tooltip title="Manage schedule">
                                <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<Iconify icon="solar:calendar-bold" width={14} />}
                                    onClick={() => navigate(PATH_DASHBOARD.user.practitionerSchedule(row.id))}
                                    sx={{
                                        textTransform: 'none',
                                        fontSize: '12px',
                                        fontWeight: 600,
                                        borderColor: '#e2e8f0',
                                        color: '#0C53B7',
                                        borderRadius: '8px',
                                        px: 1.5,
                                        py: 0.5,
                                        '&:hover': { borderColor: '#0C53B7', bgcolor: 'rgba(12,83,183,0.06)' },
                                    }}
                                >
                                    Schedule
                                </Button>
                            </Tooltip>
                        )}
                        <Tooltip title="Edit user">
                            <IconButton onClick={() => onEditRow()} disabled={adminUser !== 'ADMIN' && row.role === 'ADMIN'} size="small">
                                <Iconify icon={'solar:pen-bold'} width={18} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </TableCell>
            }
            {inviteUser && <TableCell align="right">
                <Tooltip title="Remove invitation">
                    <IconButton onClick={() => onDeleteRow()} color="error" size="small">
                        <Iconify icon={'solar:trash-bin-trash-bold'} width={18} />
                    </IconButton>
                </Tooltip>
            </TableCell>}
        </TableRow>
    );
}

UserTableListRow.propTypes = {
    row: PropTypes.object,
    selected: PropTypes.bool,
    onSelectRow: PropTypes.func,
    onEditRow: PropTypes.func,
    onDeleteRow: PropTypes.func,
    inviteUser: PropTypes.bool,
    handleSwitchChange: PropTypes.any,
    adminUser: PropTypes.any,
    currentUser: PropTypes.any,
};